import { useEffect } from 'react';

import { Tabs, Tab, Divider } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { userInfoAtom } from '~atoms';
import { useAuthorize } from '~features/authentication';

import ConsumersManagement from './ConsumersManagement';
import Header from './Header';
import Items from './Items';
import Management from './Management';
import { useStyles } from './style';
import { Page } from '../../components';
import { useError, usePrevious } from '../../shared/hooks';

interface ItemManagementProps {
	addInstance?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
}

const ItemManagement = ({ addInstance, ...props }: ItemManagementProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isSuperAdmin } = useAuthorize();
	const userInfo = useAtomValue(userInfoAtom);

	const { tab } = useParams();

	const classes = useStyles();

	const handleTabsChange = (event, value) => {
		navigate(`../${value}`, { relative: 'path' });
	};

	const tabs = [
		{ value: 'items', label: 'ui.category.items' },
		...(!isSuperAdmin() ?
			[{ value: 'itemSharedWithMe', label: 'views.assets.tab.itemSharedWithMe' }]
		:	[]),
		{ value: 'publicItems', label: 'views.assets.tab.publicItems' },
		{ value: 'management', label: 'ui.tab.management' },
	];
	// import.meta.env.VITE_APP_ENV === 'development'
	const usePreviousInstance = usePrevious(addInstance);
	const itemInstanceErrorHandling = useError({
		value: addInstance,
		message: t('views.addItem.message.success.addItemInstance'),
		variant: 'success',
	});

	useEffect(() => {
		if (usePreviousInstance && addInstance && usePreviousInstance.loading) {
			itemInstanceErrorHandling.setStartAction(true);
		}
	}, [usePreviousInstance]);

	if (!tab) {
		return <Navigate to={'/item-management/items'} />;
	}

	if (!tabs.find((t) => t.value === tab)) {
		return <Navigate to='/errors/error-404' />;
	}

	const headerButtons = [
		{ key: 'items', className: classes.header },
		{ key: 'itemSharedWithMe', className: classes.header },
		{ key: 'publicItems', className: classes.header },
		{ key: 'management', className: classes.header },
	];

	return (
		<Page className={classes.root} title={t('views.assets.page.title')}>
			{headerButtons.map((item) => item.key === tab && <Header key={item.key} {...item} />)}
			<Tabs
				className={classes.tabs}
				indicatorColor='primary'
				onChange={handleTabsChange}
				scrollButtons='auto'
				value={tab}
				variant='scrollable'
			>
				{tabs.map((tab) => (
					<Tab key={tab.value} label={t(tab.label)} value={tab.value} />
				))}
			</Tabs>
			<Divider className={classes.divider} />
			<div className={classes.content}>
				{tab === 'items' && (
					<Items itemAccess={'private'} itemUse='privateItems' itemsOfOrganisation={true} />
				)}
				{tab === 'itemSharedWithMe' && (
					<Items
						itemAccess={'public'}
						itemSharedWithMe={true}
						itemUse='sharedWithMe'
						organisationId={userInfo.organisation.id}
					/>
				)}
				{tab === 'publicItems' && (
					<Items itemAccess={'public'} itemUse='publicItems' itemsOfOrganisation={true} />
				)}
				{tab === 'management' && (isSuperAdmin() ? <Management /> : <ConsumersManagement />)}
			</div>
		</Page>
	);
};

const mapStateToProps = (state) => {
	return {
		addInstance: state.details.addInstance,
	};
};

export default connect(mapStateToProps)(ItemManagement);
